/**
 * TEAM ACCESS (EXPERT-ROOMS.md §4, E8 X2) — in an expert team's LEAD room: each department room, what its Team
 * declared it needs (least privilege), each need's status in THAT room, and — for the Lead's run roles — attach the
 * connection it should use (only declared needs; the platform re-checks it). Additive section.
 */
import React, { useState } from 'react';

const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';
const STATE = { connected: 'connected', missing: 'not connected', off: 'off', 'per-agent': 'set per agent',
  available: 'available' };

function NeedRow({ team, room, need, canSet, onSet, loadConnections }) {
  const [opts, setOpts] = useState(null);
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const st = (need.status || {}).state || '';
  const open = async () => {
    setErr('');
    try { setOpts(await loadConnections(need.capability, room)); } catch (e) { setErr(e?.message || "Couldn't list connections."); }
  };
  const send = async (req) => {
    setBusy(true); setErr('');
    try { await onSet({ team, capability: need.capability, ...req }); setOpts(null); } catch (e) { setErr(e?.message || "Couldn't do that."); }
    setBusy(false);
  };
  return (
    <div className="mt-1 text-[11px]" data-testid={`team-need-${team}-${need.capability}`}>
      <span className="font-medium text-gray-800">{need.capability}</span>
      {need.tier ? <span className="text-gray-500"> · {need.tier}</span> : null}
      <span className={st === 'connected' ? ' text-emerald-700' : ' text-amber-700'}> · {STATE[st] || 'unknown'}</span>
      {need.status?.source ? <span className="text-gray-500"> ({need.status.source})</span> : null}
      {need.why ? <p className="text-gray-500">{need.why}</p> : null}
      {need.viaRoot ? (
        <p className="text-gray-500" data-testid={`team-need-via-root-${team}-${need.capability}`}>
          Through the workspace&apos;s organization connection — used only for work a person approved.</p>
      ) : null}
      {canSet && room && !need.viaRoot ? (
        <div className="mt-0.5 flex flex-wrap items-center gap-1">
          {opts === null ? (
            <button type="button" className={`${btn} text-indigo-700 hover:bg-indigo-50`} onClick={open}
              data-testid={`team-need-choose-${team}-${need.capability}`}>Choose connection</button>
          ) : (
            <>
              <select value={pick} onChange={(e) => setPick(e.target.value)} className="rounded border border-gray-300 px-1 py-0.5"
                aria-label="Connection" data-testid={`team-need-select-${team}-${need.capability}`}>
                <option value="">{opts.length ? 'Pick a tested connection…' : 'No tested connection this room may use'}</option>
                {opts.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
              <button type="button" disabled={!pick || busy} className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50`}
                onClick={() => send({ connectionId: pick })} data-testid={`team-need-attach-${team}-${need.capability}`}>Attach</button>
            </>
          )}
          {st === 'connected' ? (
            <button type="button" disabled={busy} className={`${btn} text-gray-600 hover:bg-gray-100`}
              onClick={() => send({ remove: true })}>Detach</button>
          ) : null}
        </div>
      ) : null}
      {err ? <p className="text-rose-600">{err}</p> : null}
    </div>
  );
}

export default function RoomTeamAccess({ value, onSet, loadConnections }) {
  const v = value || {};
  return (
    <div className="space-y-2 text-[12px]" data-testid="room-team-access">
      <p className="text-[11px] text-gray-500">
        What each department room may use — only what its Team declares, nothing more.
      </p>
      {(v.teams || []).map((t) => (
        <div key={t.team} className="rounded-md border border-gray-200 px-2 py-1.5" data-testid={`team-access-${t.team}`}>
          <p className="text-[11px] font-medium text-gray-900">{t.team}
            <span className="font-normal text-gray-500"> · {t.room || 'no room yet'}</span></p>
          {(t.needs || []).length ? t.needs.map((n) => (
            <NeedRow key={`${n.capability}:${n.tier || ''}`} team={t.team} room={t.room} need={n} canSet={!!v.canSet}
              onSet={onSet} loadConnections={loadConnections} />
          )) : <p className="text-[11px] text-gray-500">Declares no platform access.</p>}
        </div>
      ))}
    </div>
  );
}
